<template>
  <div class="p-3" style="max-width: 600px; margin: 0 auto; width: 100%;">
    <div v-if="uniqueSessionDrafts.length > 0">
      <Message v-for="draft in uniqueSessionDrafts" :key="draft.inventoryId" severity="warn" :closable="false">
        <div class="ps-3">
          <h3 class="mt-0 mb-2">Unsaved inventory in progress</h3>
          <div class="mb-3">{{ draftLabel(draft.inventoryId) }}</div>
          <router-link :to="inventoryLink(draft.inventoryId)" class="text-decoration-none">
            <Button label="Continue" class="p-button-warning" />
          </router-link>
        </div>
      </Message>
    </div>

    <div v-if="inventoryTemplates.length === 0" class="text-muted mb-3">No inventory templates available</div>
    <div class="d-flex gap-4 flex-wrap" v-else>
      <div v-for="template in inventoryTemplates" :key="template.id" class="card w-100 d-flex flex-column">
        <h3 class="mt-0">{{ template.template_name }}</h3>
        <div class="text-muted mb-4">
          {{ template.template_description }}
        </div>
        <Button label="Start" class="w-100" :loading="creating" :disabled="creating"
          @click="createFromTemplate(template)" />
      </div>

      <div class="card w-100 d-flex flex-column">
        <h3 class="mt-0">Custom Inventory</h3>
        <div class="text-muted mb-4">
          For specific situations
        </div>
        <Button label="Start" class="w-100 p-button-secondary" :disabled="creating"
          @click="$refs.inventoryForm.show()" />
      </div>
    </div>


    <template v-if="realInventories.length > 0">
      <h2 class="mb-4 mt-5 text-center">Previous Inventories</h2>
      <div class="d-flex flex-column gap-3">
        <div v-for="inventory in sortedInventories" :key="inventory.id"
          class="card py-3 d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center gap-2">
            <span class="text-capitalize">{{ inventory.day_label }}</span>
            <Tag v-if="draftInventoryIds[inventory.id]" value="Unsaved" severity="warning" />
          </div>
          <div>
            <span class="text-muted">
              {{ inventory.template_name }}
            </span>
            <span class="text-muted text-nowrap"
              :title="`${inventory.completion.completed} / ${inventory.completion.total} products`">
              - {{ inventory.completion.percent }}%
            </span>
          </div>
          <div class="d-flex gap-2">
            <router-link :to="inventoryLink(inventory.id)">
              <Button icon="pi pi-pencil" class="p-button-text p-button-primary" />
            </router-link>
            <Button icon="pi pi-trash" class="p-button-text p-button-danger" @click="destroy(inventory)" />
          </div>
        </div>
      </div>
    </template>
  </div>

  <InventoryNewDialog ref="inventoryForm" />
</template>

<script>
import { computed } from 'vue'
import Tag from 'primevue/tag'
import Message from 'primevue/message'
import InventoryNewDialog from './InventoryNewDialog.vue'
import StockMixin from '@/services/stocks-mixin'
import { listDraftsForSession, clearDraft } from '@/services/inventory-draft-storage'

export default {
  mixins: [StockMixin],
  components: { InventoryNewDialog, Tag, Message },
  provide() {
    return {
      sessionDays: computed(() => this.sessionDays),
      stockDays: computed(() => this.stockDays),
    }
  },
  data() {
    return {
      sessionDrafts: [],
      creating: false,
    }
  },
  mounted() {
    this.refreshDrafts()
  },
  activated() {
    this.refreshDrafts()
  },
  computed: {
    session() {
      return this.$root.session
    },
    inventoryTemplates() {
      return this.sessionInventories
        .filter((inv) => inv.is_template === true)
    },
    realInventories() {
      return this.sessionInventories
        .filter((inv) => !inv.is_template && inv.template_name)
        .map((inv) => {
          const result = { ...inv }
          const dayObject = this.stockDays.find((d) => d.id == inv.day) || {}
          result.day_label = dayObject.dateHeader || ''
          result.day_date = dayObject.date || new Date(0)
          result.completion = this.inventoryCompletion(inv)
          return result
        })
    },
    sortedInventories() {
      return [...this.realInventories].sort((a, b) => {
        return new Date(b.day_date) - new Date(a.day_date)
      })
    },
    draftInventoryIds() {
      return this.sessionDrafts.reduce((result, draft) => {
        result[draft.inventoryId] = true
        return result
      }, {})
    },
    uniqueSessionDrafts() {
      const seen = {}
      return this.sessionDrafts.filter((draft) => {
        const id = String(draft.inventoryId)
        if (seen[id]) return false
        seen[id] = true
        return true
      })
    },
  },
  methods: {
    refreshDrafts() {
      this.sessionDrafts = listDraftsForSession(this.$route.params.id)
    },
    inventoryLink(inventoryId) {
      return {
        name: 'session_inventory',
        params: { id: this.$route.params.id, inventory_id: inventoryId },
        query: { public: 'true' },
      }
    },
    draftLabel(inventoryId) {
      const inventory = this.realInventories.find((inv) => inv.id == inventoryId)
      if (!inventory) return `Inventory #${inventoryId}`
      const parts = [inventory.day_label, inventory.template_name].filter(Boolean)
      return parts.join(' — ') || `Inventory #${inventoryId}`
    },
    valuesForInventory(inventory) {
      const draft = this.sessionDrafts.find((d) => String(d.inventoryId) === String(inventory.id))
      if (draft?.values) return draft.values
      return inventory.values || {}
    },
    productsForInventory(inventory) {
      let result = this.sessionProducts
        .map((productId) => this.$root.getProduct(productId))
        .filter((product) => product.id)
      if ((inventory.supplier_ids || []).length > 0) {
        result = result.filter((product) => inventory.supplier_ids.includes(product.supplier_id))
      }
      if ((inventory.product_category_ids || []).length > 0) {
        result = result.filter((product) => inventory.product_category_ids.includes(product.category_id))
      }
      return result
    },
    areasForInventory(inventory, products) {
      let areas = Object.values(this.$root.categories)
        .filter((c) => c.type === 'StorageArea')
      if ((inventory.storage_area_ids || []).length > 0) {
        areas = areas.filter((area) => inventory.storage_area_ids.includes(area.id))
      } else {
        areas = areas.concat([{ id: 'other' }])
        areas = areas.filter((area) => products.some((product) => {
          const areaIds = product.storage_area_ids || []
          if (area.id === 'other') return areaIds.length === 0
          return areaIds.includes(area.id)
        }))
      }
      return areas
    },
    inventoryCompletion(inventory) {
      const products = this.productsForInventory(inventory)
      const areas = this.areasForInventory(inventory, products)
      const values = this.valuesForInventory(inventory)
      let total = 0
      let completed = 0
      areas.forEach((area) => {
        const areaProducts = products.filter((product) => {
          const areaIds = product.storage_area_ids || []
          if (area.id === 'other') return areaIds.length === 0
          return areaIds.includes(area.id)
        })
        total += areaProducts.length
        completed += areaProducts.filter(
          (product) => (values[product.id] || {})[area.id]?.value != null
        ).length
      })
      const percent = total === 0 ? 0 : Math.round((completed / total) * 100)
      return { completed, total, percent }
    },
    async createFromTemplate(template) {
      if (this.creating) return
      const today = this.stockDays.find((day) => day.date.isToday())
      if (!today) {
        this.toastError(`The current session "${this.$root.session.name}"" do not have an event configured for today`)
        return
      }
      const existing = this.findExistingInventoryForToday(template, today)
      if (existing) {
        this.$confirm.require({
          message: `An inventory "${template.template_name}" already exists for today. Open it or create a new one?`,
          header: 'Inventory already exists',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Open existing',
          rejectLabel: 'Create new',
          accept: () => {
            this.$router.push(this.inventoryLink(existing.id))
          },
          reject: () => {
            this.createInventoryFromTemplate(template, today)
          },
        })
        return
      }
      await this.createInventoryFromTemplate(template, today)
    },
    findExistingInventoryForToday(template, today) {
      return this.realInventories
        .filter((inv) => inv.day == today.id && inv.template_name === template.template_name)
        .sort((a, b) => Number(b.id) - Number(a.id))[0] || null
    },
    async createInventoryFromTemplate(template, today) {
      if (this.creating) return
      this.creating = true
      const inventory = {
        day: today.id,
        session_id: this.$root.session.id,
        storage_area_ids: [...(template.storage_area_ids || [])],
        product_category_ids: [...(template.product_category_ids || [])],
        supplier_ids: [...(template.supplier_ids || [])],
        completed_storage_areas_ids: [],
        values: {},
        template_name: template.template_name,
      }
      const created = await this.dbCreate('inventories', inventory, (createdInventory) => {
        this.$router.push(this.inventoryLink(createdInventory.id))
      })
      if (!created) this.creating = false
    },
    destroy(inventory) {
      this.$confirm.require({
        message: `Are you sure you want to delete this inventory ?`,
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
          const inventoryId = inventory.id
          const deleted = await this.dbDestroy('inventories', inventory)
          if (!deleted) return
          clearDraft(inventoryId)
          this.refreshDrafts()
        },
      })
    },
    onInventoryCreated(inventory) {
      // Navigate to the inventory with public mode
      this.$router.push({
        name: 'session_inventory',
        params: { id: this.$route.params.session_id, inventory_id: inventory.id },
        query: { public: 'true' },
      })
    },
  }
}
</script>

<style lang='scss' scoped>
.card {
  padding: 2rem;
}
</style>
